<template>
  <l-default
    v-if="!product"
    :class="b()"
  >
    {{ $t('l-article-detail.product-not-found') }}
  </l-default>
  <l-default
    v-else
    :class="b()"
  >
    <c-container>
      <c-breadcrumb :class="b('breadcrumb')" />
      <h1 :class="b('title')">{{ product.name }}</h1>

      <div :class="b('wrapper')">
        <div :class="b('reference-codes')">
          <div :class="b('reference-code')">
            <strong>{{ $t('l-article-detail.label-sku') }}</strong>
            {{ product.sku }}
          </div>

          <div
            v-if="product.sapEnumber"
            :class="b('reference-code')"
          >
            <strong>{{ $t('l-article-detail.label-sap-enumber') }}</strong>
            {{ product.sapEnumber }}
          </div>
          <div
            v-if="product.sapNumberEan"
            :class="b('reference-code')"
          >
            <strong>{{ $t('l-article-detail.label-sap-number-ean') }}</strong>
            {{ product.sapNumberEan }}
          </div>
        </div>
        <c-favorite-product-toggle
          v-if="favoritesProduct"
          :class="b('favorite-toggle')"
          :product="favoritesProduct"
        />
      </div>

      <div :class="b('main-details-wrapper')">
        <div :class="b('image-gallery')">
          <c-product-image
            :images="product?.images || []"
            :text-image-overlay="product.textImageOverlay"
            :label="product.availabilityLabel"
            show-as-gallery
          />
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div :class="b('description')">
          <div v-html="product.textDescription" />
          <div
            v-if="product.textAlert"
            v-html="product.textAlert"
            :class="b('alert')"
          ></div>
          <div
            v-if="product.textReplacementInfo"
            v-html="product.textReplacementInfo"
            :class="b('replacement-info')"
          ></div>
          <div
            v-if="product.ctas?.length"
            :class="b('call-to-actions')"
          >
            <e-button
              v-for="(cta, index) in product.ctas"
              :key="index"
              :class="b('cta')"
              variant="secondary"
              :href="cta.url"
              :target="cta.target || '_blank'"
              :title="cta.title"
            >
              {{ cta.label }}
            </e-button>
          </div>
        </div>
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <div
        v-if="contentSections.length"
        :class="b('content-wrapper')"
      >
        <c-content-navigation
          v-if="!$viewport.isMobile"
          :sections="contentSections"
          sticky
          @update:anchor-offset="updateAnchorOffset"
        />
        <div
          v-for="contentSection in contentSections"
          :key="contentSection.url"
          :class="b('content-section')"
        >
          <c-content-section-anchor
            :anchor-id="contentSection.url"
            :offset="anchorScrollOffset"
          />
          <c-collapse
            :collapsed="contentSection.collapse"
            :use-as-plain-content="!$viewport.isMobile"
            :class="b('collapse')"
          >
            <template #title>
              <component
                :is="$viewport.isMobile ? 'span' : 'h2'"
                :class="b('collapse-header')"
              >
                {{ contentSection.headline }}
              </component>
            </template>
            <template #content>
              <div :class="b('collapse-content')">
                <div
                  v-if="contentSection.key === contentSectionTypes.TechnicalFeatures"
                  :class="b('technical-features-wrapper')"
                >
                  <div :class="b('technical-features')">
                    <div
                      v-for="(technicalFeature, index) in product?.technicalFeatures"
                      :key="index"
                      :class="b('technical-feature-item')"
                    >
                      <h3 :class="b('technical-feature-item-title')">{{ technicalFeature.headline }}</h3>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <p v-html="technicalFeature.value"></p>
                    </div>
                  </div>
                  <template v-if="product.compoundArticles?.length">
                    <div :class="b('compound-article-info')">
                      {{ $t('l-article-detail.compound-article-info') }}
                    </div>
                    <div :class="b('compound-articles-wrapper')">
                      <div
                        v-for="(article, index) in product.compoundArticles"
                        :key="index"
                        :class="b('compound-article')"
                      >
                        <div :class="b('compound-article-name')">{{ article.name }}</div>
                        <div :class="b('technical-features')">
                          <div
                            v-for="(technicalFeature, idx) in article.technicalFeatures"
                            :key="idx"
                            :class="b('technical-feature-item')"
                          >
                            <h3 :class="b('technical-feature-item-title')">{{ technicalFeature.headline }}</h3>
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <p v-html="technicalFeature.value"></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.AlternativeProducts"
                  :class="b('related-products')"
                >
                  <c-related-product
                    v-for="productItem in product?.alternativeProducts"
                    :key="productItem.id"
                    :product="productItem"
                  />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.AccessoryProducts"
                  :class="b('related-products')"
                >
                  <c-related-product
                    v-for="productItem in product?.accessoryProducts"
                    :key="productItem.id"
                    :product="productItem"
                  />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.Downloads"
                  :class="b('downloads')"
                >
                  <c-download-list
                    v-if="product?.downloads?.length"
                    :items="product?.downloads"
                  />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.Links"
                  :class="b('links')"
                >
                  <c-link-list
                    v-if="product.links?.length"
                    :items="product.links"
                  />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.SoftwareVersions"
                  :class="b('software-versions')"
                >
                  <c-software-versions :software-versions="product?.softwareVersions" />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.Variants"
                  :class="b('variants')"
                >
                  <div :class="b('variants-list')">
                    <c-product-variant
                      v-for="productVariant in visibleVariants"
                      :key="productVariant.id"
                      :highlight="product.id === productVariant.id"
                      :product="productVariant"
                    />
                  </div>
                  <e-button
                    v-if="hasMoreVariants"
                    :class="b('button-variants-show-all')"
                    variant="primary"
                    spacing="sm"
                    @click="showAllVariants"
                  >
                    {{ $t('l-article-detail.show-all-variants') }}
                  </e-button>
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.Applications"
                  :class="b('applications')"
                >
                  <em>
                    {{
                      $t('l-article-detail.applications-amount', {
                        total: product?.applications?.pagination.totalCount,
                      })
                    }}
                  </em>
                  <c-product-bundle-list
                    v-if="product?.applications?.data.length"
                    :product-id="product?.id"
                    :group-id="product?.groupId"
                    :initial-list="product?.applications?.data"
                    :pagination="product?.applications?.pagination"
                  />
                </div>
                <div
                  v-else-if="contentSection.key === contentSectionTypes.SetsAndKits"
                  :class="b('sets-and-kits')"
                >
                  <em>
                    {{
                      $t('l-article-detail.sets-and-kits-amount', {
                        total: product?.setsAndKits?.pagination.totalCount,
                      })
                    }}
                  </em>
                  <c-product-set-list
                    v-if="product?.setsAndKits?.data.length"
                    :product-id="product?.id"
                    :initial-list="product?.setsAndKits?.data"
                    :pagination="product?.setsAndKits?.pagination"
                  />
                </div>
              </div>
            </template>
          </c-collapse>
        </div>
      </div>
    </c-container>
  </l-default>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import { INITIAL_VARIANTS_SHOW_COUNT } from '@/setup/globals';
  import useProductStore from '@/stores/product';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cCollapse from '@/components/c-collapse.vue';
  import cContainer from '@/components/c-container.vue';
  import cContentNavigation, { ContentSection } from '@/components/c-content-navigation.vue';
  import cContentSectionAnchor from '@/components/c-content-section-anchor.vue';
  import cDownloadList from '@/components/c-download-list.vue';
  import cFavoriteProductToggle from '@/components/c-favorite-product-toggle.vue';
  import cLinkList from '@/components/c-link-list.vue';
  import cProductBundleList from '@/components/c-product-bundle-list.vue';
  import cProductImage from '@/components/c-product-image.vue';
  import cProductSetList from '@/components/c-product-set-list.vue';
  import cProductVariant from '@/components/c-product-variant.vue';
  import cRelatedProduct from '@/components/c-related-product.vue';
  import cSoftwareVersions from '@/components/c-software-versions.vue';
  import eButton from '@/elements/e-button.vue';
  import lDefault from '@/layouts/l-default.vue';
  import { FavoritesProduct, Product, ProductBase } from '@/types/product';

  type ContentSectionCollapse = ContentSection & {
    key: string;
    headline: string;
    collapse?: boolean;
  };

  enum ContentSectionTypes {
    TechnicalFeatures = 'technicalFeatures',
    AlternativeProducts = 'alternativeProducts',
    AccessoryProducts = 'accessoryProducts',
    Downloads = 'downloads',
    Links = 'links',
    Variants = 'variants',
    Applications = 'applications',
    SetsAndKits = 'setsAndKits',
    SoftwareVersions = 'softwareVersions',
  }

  type Setup = {
    contentSectionTypes: typeof ContentSectionTypes;
    productStore: ReturnType<typeof useProductStore>;
  };

  type Data = {
    anchorScrollOffset: number;
    visibleVariantsCount: number;
  };

  export default defineComponent({
    name: 'l-article-detail',
    components: {
      cProductImage,
      cContainer,
      cProductBundleList,
      cProductSetList,
      cProductVariant,
      cRelatedProduct,
      cDownloadList,
      cLinkList,
      cSoftwareVersions,
      cCollapse,
      cContentSectionAnchor,
      cContentNavigation,
      cBreadcrumb,
      cFavoriteProductToggle,
      eButton,
      lDefault,
    },

    // components: {},

    // props: {},

    setup(): Setup {
      return {
        contentSectionTypes: ContentSectionTypes,
        productStore: useProductStore(),
      };
    },
    data(): Data {
      return {
        anchorScrollOffset: 0,
        visibleVariantsCount: INITIAL_VARIANTS_SHOW_COUNT,
      };
    },

    computed: {
      product(): Product | null {
        return this.productStore.product;
      },

      favoritesProduct(): FavoritesProduct | null {
        if (!this.product) {
          return null;
        }

        return {
          id: this.product.id,
          mainImage: this.product.mainImage,
          name: this.product.name ?? '',
          sku: this.product.sku ?? '',
          url: this.product.url,
        };
      },

      contentSections(): ContentSectionCollapse[] {
        const sections = [] as ContentSectionCollapse[];
        const allowedSections = [
          ContentSectionTypes.SoftwareVersions,
          ContentSectionTypes.TechnicalFeatures,
          ContentSectionTypes.SetsAndKits,
          ContentSectionTypes.Variants,
          ContentSectionTypes.Downloads,
          ContentSectionTypes.Links,
          ContentSectionTypes.Applications,
          ContentSectionTypes.AlternativeProducts,
          ContentSectionTypes.AccessoryProducts,
        ];

        allowedSections.forEach((propertyName: string) => {
          const propertyValue = this.product?.[propertyName];
          const isArrayAndIsEmpty = Array.isArray(propertyValue) && !propertyValue?.length;
          const isBundle = [ContentSectionTypes.Applications, ContentSectionTypes.SetsAndKits].includes(
            propertyName as ContentSectionTypes,
          );
          const bundleData = (isBundle && (this.product?.[propertyName] as { data: unknown[] })?.data) || [];

          if (
            (isBundle && bundleData?.length) ||
            (!isBundle && this.product && propertyValue !== null && !isArrayAndIsEmpty)
          ) {
            sections.push({
              key: propertyName,
              title: this.$t(`l-article-detail.in-page-navigation.${propertyName}`),
              headline: this.$t(`l-article-detail.section-headline.${propertyName}`),
              url: this.$t(`l-article-detail.in-page-navigation.${propertyName}`),
            });
          }
        });

        return sections;
      },

      variantsCount(): number {
        return this.product?.variants?.length || 0;
      },

      visibleVariants(): ProductBase[] {
        return this.product?.variants?.slice(0, this.visibleVariantsCount) || [];
      },

      hasMoreVariants(): boolean {
        return this.visibleVariants.length < this.variantsCount;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      setTimeout(() => {
        this.scrollToActiveOnLoad();
      }, 1000);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      updateAnchorOffset(offset: number) {
        this.anchorScrollOffset = offset || 0;
      },

      showAllVariants(): void {
        this.visibleVariantsCount = this.variantsCount;
      },

      /**
       * Scroll to the active element if the URL matches one of the sections.
       */
      scrollToActiveOnLoad() {
        const currentHash = window.location.hash.replace('#', '');

        if (!currentHash) {
          return;
        }

        // Scroll the content section into view if needed
        const targetSection = document.getElementById(currentHash);

        if (targetSection) {
          targetSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .l-article-detail {
    padding: 3rem 0;

    &__breadcrumb {
      margin-bottom: 3rem;
    }

    &__title {
      margin-bottom: 1.5rem;
    }

    &__wrapper {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;

      @include mixins.media(sm) {
        grid-template-columns: 1fr auto;
        align-items: start;
      }
    }

    &__favorite-toggle {
      order: -1;
      justify-self: start;

      @include mixins.media(sm) {
        order: 0;
        justify-self: end;
      }
    }

    &__main-details-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 3rem;
      margin-top: 2.5rem;

      @include mixins.media(sm) {
        display: grid;
        grid-template-columns: 18rem 1fr;
        margin-top: 3.5rem;
        gap: 2rem;
      }

      @include mixins.media(md) {
        grid-template-columns: 24rem 1fr;
        gap: 5rem;
      }
    }

    &__description {
      @include mixins.list-styles;

      display: flex;
      flex-direction: column;
      gap: 1.6rem;

      ul {
        padding-inline-start: 1.5rem;
      }
    }

    &__call-to-actions {
      display: flex;
      flex-flow: row wrap;
      gap: 16px;
      align-items: center;
      justify-content: flex-end;
    }

    &__alert {
      color: variables.$va-color-status--error;
    }

    &__content-wrapper {
      position: relative;
      margin-top: 3rem;

      @include mixins.media(md) {
        margin-top: 4rem;
      }
    }

    &__content-section {
      &:not(:last-child) {
        border-bottom: 1px solid variables.$va-color-grayscale--0;
      }
    }

    &__collapse {
      .c-collapse__title {
        padding-top: 3rem;
        padding-bottom: 2rem;
      }
    }

    &__collapse-header {
      @include mixins.heading-h2();
    }

    &__collapse-content {
      overflow-wrap: break-word;
      padding-bottom: 4rem;

      @include mixins.media(md) {
        padding-bottom: 8rem;
      }
    }

    &__technical-features-wrapper {
      display: flex;
      flex-direction: column;
      gap: 2.5rem;
    }

    &__technical-features {
      @include mixins.list-styles;

      white-space: pre-wrap;

      @include mixins.media(sm) {
        column-count: 2;
        column-gap: 2rem;
      }

      ul {
        margin-left: 1.2rem;
      }
    }

    &__technical-feature-item {
      display: flex;
      flex-direction: column;
      break-inside: avoid;
      margin-bottom: 1rem;
    }

    &__technical-feature-item-title {
      @include mixins.heading-h4();
    }

    &__compound-article-info {
      padding: 1.75rem 0;
      border-top: 1px solid var(--theme-color-grayscale--650);
    }

    &__compound-articles-wrapper {
      display: flex;
      flex-direction: column;
      gap: 3.25rem;
    }

    &__compound-article-name {
      /* stylelint-disable order/order */
      margin-bottom: 1.75rem;

      @include mixins.heading-h3;
    }

    &__related-products {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;

      @include mixins.media(sm) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    &__variants {
      display: flex;
      flex-direction: column;
    }

    &__variants-list {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 1rem;

      @include mixins.media(sm) {
        grid-template-columns: repeat(2, 1fr);
      }

      @include mixins.media(md) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    &__button-variants-show-all {
      margin: 3rem auto 0;
    }

    &__sets-and-kits,
    &__applications {
      display: flex;
      flex-direction: column;
      gap: 3rem;
    }
  }
</style>
